import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { HYDRATE } from 'next-redux-wrapper';
// Using require() instead of ES6 import to prevent SSR errors - Datadog RUM SDK is browser-only
const datadogRum = require('@datadog/browser-rum').datadogRum;
import APIClient from '../../helpers/ApiClient';
import { updateTemplateState } from '../../helpers/TemplateStateHelper';
import config from './../../config';
import logger from '../../logger/logger';

// REDUX TYPES
interface ITemplateLoad {
  id: string;
  collectionId: string;
}

interface ITemplateLoadTimeframe extends ITemplateLoad {
  selectedHeaders: string[];
}

// TEMPLATE TYPES
export const initialState: any = {
  id: '',
  name: '',
  note: '',
  methodology: '',
  additionalNote: '',
  publicationDate: '',
  lastModifiedTimestamp: '',
  source: '',
  chartedLength: 0,
  comparativeEstimates: [],
  titleInTable: '',
  subtitleInTable: '',
  tableRows: [],
  metricScaleMode: '',
  maxReached: false,
  maximumHeaders: 5,
  period: 0,
  availableTimes: [],
  headers: [],
  charted: [],
  chartYear: null,
  chartType: '',
  titleInChart: '',
  subtitleInChart: '',
  geography: '',
  tablerows: null,
  data: null,
  logoType: '',
  loading: false,
  loaded: false,
  topics: [],
};

// Thunk API Calls
export const template_load = createAsyncThunk('get/collectiontemplate', async (params: ITemplateLoad) => {
  const { id, collectionId } = params;
  const url = `${config.apiHost}/collectiontemplate?id=${id}&collectionid=${collectionId}`;
  try {
    const res = await APIClient(`get`, url);
    return res.data;
  } catch (error: any) {
    const errorContext = {
      source: 'templateSlice',
      action: 'template_load',
      templateId: id,
      collectionId,
      url,
      status: error.response?.status,
      statusText: error.response?.statusText,
      errorCode: error.code,
    };

    // Use datadogRum in browser, pino logger on server (SSR)
    if (globalThis.window !== undefined && datadogRum && datadogRum.addError !== undefined) {
      datadogRum.addError(error, errorContext);
    } else {
      logger.error({ err: error, ...errorContext }, 'Failed to load template');
    }
    throw error;
  }
});

export const template_load_timeframe = createAsyncThunk(
  'get/collectiontemplatetimeframe',
  async (params: ITemplateLoadTimeframe) => {
    const { id, selectedHeaders, collectionId } = params;

    const sortedHeaders = [...selectedHeaders].sort((a, b) => {
      const A = a.split(' ')[0].split('/');
      const B = b.split(' ')[0].split('/');
      const strA = [...A].reverse().join('/');
      const strB = [...B].reverse().join('/');
      return strA.localeCompare(strB);
    });

    const url = `${config.apiHost}/collectiontemplate?id=${id}&dates=${sortedHeaders}&collectionid=${collectionId}`;
    try {
      const res = await APIClient(`get`, url);
      return res.data;
    } catch (error: any) {
      const errorContext = {
        source: 'templateSlice',
        action: 'template_load_timeframe',
        templateId: id,
        collectionId,
        selectedHeaders: sortedHeaders,
        url,
        status: error.response?.status,
        statusText: error.response?.statusText,
        errorCode: error.code,
      };

      // Use datadogRum in browser, pino logger on server (SSR)
      if (globalThis.window !== undefined && datadogRum && datadogRum.addError !== undefined) {
        datadogRum.addError(error, errorContext);
      } else {
        logger.error({ err: error, ...errorContext }, 'Failed to load template timeframe');
      }
      throw error;
    }
  },
);

export const templateSlice = createSlice({
  name: 'template',
  initialState,
  reducers: {
    template_table_toggle_row: (state, action: PayloadAction<string>) => {
      if (state.charted[action.payload].IsCharted) {
        state.charted[action.payload].IsCharted = false;
      } else {
        if (!state.maxReached) {
          state.charted[action.payload].IsCharted = true;
        }
      }

      if (
        Object.values(state.charted).filter((x: any) => x.IsCharted).length >= 2 &&
        state.chartType !== 'horizontalBar'
      ) {
        state.maxReached = true;
      } else {
        state.maxReached = false;
      }
    },
    template_chart_set_title: (state, action: PayloadAction<string>) => {
      if (action.payload !== '') {
        state.titleInChart = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(HYDRATE, (state, action: any) => {
      return {
        ...state,
        ...action.payload.template,
      };
    });
    builder.addCase(template_load.fulfilled, (state, action) => {
      updateTemplateState(state, action.payload);
    });
    builder.addCase(template_load.rejected, (state, action) => {
      state.error = action.payload;
    });
    builder.addCase(template_load.pending, (state, action) => {
      state.loading = true;
      state.loaded = false;
    });
    builder.addCase(template_load_timeframe.fulfilled, (state, action) => {
      updateTemplateState(state, action.payload);
    });
    builder.addCase(template_load_timeframe.rejected, (state, action) => {
      state.error = action.payload;
    });
    builder.addCase(template_load_timeframe.pending, (state, action) => {
      state.loading = true;
      state.loaded = false;
    });
  },
});

// Select Property
export const selectTemplate = (state) => state.template;

// Action creators are generated for each case reducer function
export const { template_chart_set_title, template_table_toggle_row } = templateSlice.actions;

export default templateSlice.reducer;
